/**
 * Компонент шапки виджета
 */

import { useState } from 'react';
import { Phone, Minus, X, ArrowLeft } from 'lucide-react';
import styles from './ChatHeader.module.css';
import { ContactsDropdown } from '../ContactsDropdown';
import { LarteLogo } from '../Logo';

/**
 * Пропсы компонента ChatHeader
 */
interface ChatHeaderProps {
  /** Заголовок виджета */
  title: string;
  /** Статус (онлайн/оффлайн) */
  isOnline?: boolean;
  /** Текст статуса */
  statusText: string;
  /** Колбек закрытия */
  onClose: () => void;
  /** Колбек минимизации (опционально) */
  onMinimize?: () => void;
  /** Показывать кнопку контактов */
  showContacts?: boolean;
  /** Показывать кнопку "Назад" */
  showBackButton?: boolean;
  /** Колбек кнопки "Назад" */
  onBack?: () => void;
}

/**
 * Шапка виджета чата
 * 
 * @description Header с логотипом, статусом и кнопками управления
 */
export function ChatHeader({
  title,
  isOnline = true,
  statusText,
  onClose,
  onMinimize,
  showContacts = true,
  showBackButton = false,
  onBack,
}: ChatHeaderProps) {
  const [isContactsOpen, setIsContactsOpen] = useState(false);

  return (
    <header className={styles.header}>
      {/* Левая часть: лого + инфо */}
      <div className={styles.headerLeft}>
        {/* Кнопка назад (если есть сообщения) */}
        {showBackButton && onBack && (
          <button
            className={styles.backBtn}
            onClick={onBack}
            aria-label="Начать новый диалог"
            type="button"
          >
            <ArrowLeft size={20} strokeWidth={2} />
          </button>
        )}

        <div className={styles.logo}>
          <LarteLogo size={40} color="var(--accent)" />
          <span className={styles.aiBadge}>AI</span>
        </div>
        
        <div className={styles.headerInfo}>
          <h2 className={styles.title}>{title}</h2>
          <div className={styles.status}>
            <span
              className={`
                ${styles.statusIndicator}
                ${isOnline ? styles.online : styles.offline}
              `}
              aria-label={isOnline ? 'Онлайн' : 'Оффлайн'}
            />
            <span>{statusText}</span>
          </div>
        </div>
      </div>

      {/* Правая часть: кнопки действий */}
      <div className={styles.headerActions}>
        {/* Кнопка контактов */}
        {showContacts && (
          <div style={{ position: 'relative' }}>
            <button
              className={styles.headerBtn}
              onClick={() => setIsContactsOpen(!isContactsOpen)}
              aria-label="Контакты"
              aria-expanded={isContactsOpen}
              type="button"
            >
              <Phone size={18} strokeWidth={2} />
            </button>
            
            {/* Dropdown контактов */}
            {isContactsOpen && (
              <ContactsDropdown onClose={() => setIsContactsOpen(false)} />
            )}
          </div>
        )}

        {/* Кнопка минимизации */}
        {onMinimize && (
          <button
            className={styles.headerBtn}
            onClick={onMinimize}
            aria-label="Свернуть"
            type="button"
          >
            <Minus size={18} strokeWidth={2} />
          </button>
        )}

        {/* Кнопка закрытия */}
        <button
          className={styles.headerBtn}
          onClick={onClose}
          aria-label="Закрыть"
          type="button"
        >
          <X size={20} strokeWidth={2} />
        </button>
      </div>
    </header>
  );
}

